/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import type { DivisionItem } from '../../hooks/useSchedulerDivisions'
import type {
  SchedulerConstraint,
  CreateConstraintData,
} from '../../hooks/useSchedulerConstraints'
import type { Venue } from '../../hooks/useSchedulerLocations'
import type { TimeSlot } from '../../hooks/useSchedulerTimeSlots'
import type { GeneratedGame } from '../GeneratedGamesSection'
import DivisionDetailHeader from './DivisionDetailHeader'
import DivisionTeamsTable from './DivisionTeamsTable'
import DivisionConstraintsTable from './DivisionConstraintsTable'
import SchedulePreview from './SchedulePreview'
import { computeTotalGames } from '../MatchupMatrixModal'

interface DivisionDetailProps {
  division: DivisionItem
  divisions?: DivisionItem[]
  constraints?: SchedulerConstraint[]
  games?: GeneratedGame[]
  timeSlots?: TimeSlot[]
  competition?: { starts_at?: string; ends_at?: string } | null
  onToggleReady: (divisionId: string) => void
  onToggleConstraintEnabled?: (constraintId: string, enabled: boolean) => void
  onUpdateConstraintType?: (constraintId: string, isHard: boolean) => void
  onDeleteTeam?: (divisionId: string, teamId: string, teamName: string) => void
  onUpdateTeamNotes?: (
    divisionId: string,
    teamId: string,
    notes: string
  ) => Promise<void>
  onAddConstraint?: (data: CreateConstraintData) => Promise<unknown>
  onDeleteConstraint?: (constraintId: string) => Promise<void>
  venues?: Venue[]
  defaultGamesPerMatchup?: number
}

const DivisionDetail: React.FC<DivisionDetailProps> = ({
  division,
  divisions = [],
  constraints = [],
  games = [],
  timeSlots = [],
  competition = null,
  onToggleReady,
  onToggleConstraintEnabled,
  onUpdateConstraintType,
  onDeleteTeam,
  onUpdateTeamNotes,
  onAddConstraint,
  onDeleteConstraint,
  venues,
  defaultGamesPerMatchup = 1,
}) => {
  // Derive the per-pair matrix from PAIR_MATCHUP_COUNT constraints scoped
  // to this division. Constraints with no division_uids apply to all
  // divisions; division_uids array including this division also applies.
  const matrix: Record<string, number> = {}
  for (const c of constraints) {
    if (c.type.code !== 'pair_matchup_count') {
      continue
    }
    if (
      c.division_uids !== null &&
      c.division_uids.length > 0 &&
      !c.division_uids.includes(division.id)
    ) {
      continue
    }
    const teamUids = c.team_uids ?? []
    if (teamUids.length !== 2) {
      continue
    }
    const count = c.parameters.count
    if (typeof count !== 'number') {
      continue
    }
    const sorted = [teamUids[0], teamUids[1]].sort()
    matrix[`${sorted[0]}|${sorted[1]}`] = count
  }
  const gamesPerMatchup = defaultGamesPerMatchup
  const totalGames = computeTotalGames(division.teams, matrix, gamesPerMatchup)
  const hasMatrixOverrides = Object.keys(matrix).length > 0

  return (
    <div css={container}>
      <DivisionDetailHeader
        division={division}
        onToggleReady={onToggleReady}
        totalGames={totalGames}
        gamesPerMatchup={gamesPerMatchup}
        hasMatrixOverrides={hasMatrixOverrides}
      />
      <div>
        <DivisionTeamsTable
          division={division}
          constraints={constraints}
          onDeleteTeam={onDeleteTeam}
          onUpdateTeamNotes={onUpdateTeamNotes}
          onAddConstraint={onAddConstraint}
          onDeleteConstraint={onDeleteConstraint}
          onToggleConstraintEnabled={onToggleConstraintEnabled}
          onUpdateConstraintType={onUpdateConstraintType}
          divisionId={division.id}
          venues={venues}
        />
      </div>
      <DivisionConstraintsTable
        constraints={constraints}
        divisionId={division.id}
        onToggleEnabled={onToggleConstraintEnabled}
        onAddConstraint={onAddConstraint}
        onDeleteConstraint={onDeleteConstraint}
        onUpdateConstraintType={onUpdateConstraintType}
        venues={venues}
      />
      <SchedulePreview
        games={games}
        divisionId={division.id}
        venues={venues}
        divisions={divisions}
        timeSlots={timeSlots}
        constraints={constraints}
        competition={competition}
      />
    </div>
  )
}

const container = css`
  padding-bottom: 32px;
`

export default DivisionDetail
